/**
 * PrepSheetCard — the mini notebook card for a preparation request on the
 * shared pipeline. Reproduces the approved instruction-sheet mini-card: cream
 * ruled paper, a red left margin, three small punched holes, Caveat client
 * name and Kalam meta/deadline. Reads as unmistakably different from a file
 * FolderCard (manila folder) at a glance.
 *
 * Deadline urgency (computed with the shared previousBusinessDay helper via
 * src/utils/prepRequests.js):
 *   overdue          → bold + "OVERDUE" marker, strong red
 *   due today/tmrw   → brand orange
 *   otherwise        → normal red "Due:" line
 */

import React from 'react';
import { CARD_TOP_CLEARANCE } from './FolderCard';
import {
  computePrepDeadline,
  formatPrepDeadline,
  getPrepUrgency,
  prepTaskProgress,
} from '../../utils/prepRequests';

// Notebook palette — exported so the completed-prep fanned stack
// (PrepFannedStack) reuses the SAME values rather than re-typing them.
export const INK = '#1f3a5c';
export const INK_LIGHT = '#4a6182';
export const MARGIN = '#d4565a';
export const PAPER = '#fbf6e7';
export const PAPER_EDGE = '#e8dfc3';
const ORANGE = '#F2682A';
const OVERDUE_RED = '#C0392B';
const RULED = 'rgba(60, 110, 170, 0.20)';

export default function PrepSheetCard({
  prep,
  onClick,
  interactive = true,
  notesCount = 0,
  pendingAck = 0,
}) {
  const deadline = computePrepDeadline(prep);
  const urgency = getPrepUrgency(prep);
  const { done, total } = prepTaskProgress(prep);

  // Card illumination (CLAUDE.md §31): OWNER-ONLY. `pendingAck` is the count
  // of unacknowledged notes routed to the VIEWER'S ACTIVE ROLE (scoped in
  // SharedPipeline) — the sheet lights and gently flashes only for the role
  // that must acknowledge. Amber outline + breathing ring (.inbox-ack-lit-paper
  // keeps the notebook's own elevation shadows) + pinging dot on the note
  // badge. Kept distinct from the card's red deadline/urgent cues.
  const lit = pendingAck > 0;

  // Badge: plain count while Submitted; progress while In Progress;
  // "Done" once Completed.
  let taskBadge;
  if (prep.status === 'completed') {
    taskBadge = total > 0 ? `Done · ${total}/${total}` : 'Done';
  } else if (prep.status === 'in_progress') {
    taskBadge = `${done} / ${total} done`;
  } else {
    taskBadge = `${total} ${total === 1 ? 'task' : 'tasks'}`;
  }

  const dueColour = urgency === 'overdue' ? OVERDUE_RED : urgency === 'soon' ? ORANGE : MARGIN;

  // App-level role gate (CLAUDE.md §30): a non-permitted role still sees the
  // card in full, but it drops the button semantics + pointer cursor so it
  // reads as non-interactive (a subtle, light-touch cue — no dimming, no lock
  // badge). onClick still fires so the pipeline can toast the friendly message.
  const gateBind = interactive
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Open preparation request for ${prep.clientName}`,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick && onClick();
          }
        },
      }
    : {};

  return (
    <div
      onClick={onClick}
      {...gateBind}
      className={lit ? 'inbox-ack-lit-paper' : undefined}
      // Return-to-dashboard lookup (CLAUDE.md §39).
      data-card-type="prep"
      data-card-id={prep.id}
      style={{
        ...styles.card,
        cursor: interactive ? 'pointer' : 'default',
        // Test records (is_test = true, CLAUDE.md §33) get a dashed coral
        // outline (existing --cp-warning token) so they can never be
        // mistaken for real client work. Acknowledgement illumination
        // still takes precedence when lit. Real records are unchanged.
        ...(prep.isTest && !lit
          ? { outline: '2px dashed var(--cp-warning)', outlineOffset: '0px' }
          : {}),
        // When lit, drop the inline elevation shadow so the breathing-ring
        // class (whose keyframes re-include the elevation) can animate it —
        // inline box-shadow would otherwise win over the CSS animation.
        ...(lit ? { ...styles.cardLit, boxShadow: undefined } : {}),
      }}
    >
      <span style={{ ...styles.hole, top: '18px' }} aria-hidden="true" />
      <span style={{ ...styles.hole, top: '50%', transform: 'translateY(-50%)' }} aria-hidden="true" />
      <span style={{ ...styles.hole, bottom: '18px' }} aria-hidden="true" />

      {/* Notes badge — top-right; shows a pinging amber dot while any note is
          awaiting acknowledgement (CLAUDE.md §31). Count itself is unchanged. */}
      {notesCount > 0 && (
        <span
          style={styles.notesBadge}
          title={
            lit
              ? `${notesCount} note${notesCount === 1 ? '' : 's'} — ${pendingAck} awaiting acknowledgement`
              : `${notesCount} note${notesCount === 1 ? '' : 's'}`
          }
        >
          {notesCount} {notesCount === 1 ? 'note' : 'notes'}
          {lit && (
            <span style={styles.pingWrap}>
              <span className="inbox-ack-ping" style={styles.pingRing} />
              <span style={styles.pingDot} />
            </span>
          )}
        </span>
      )}

      <div style={styles.client}>{prep.clientName}</div>
      {/* TEST tag — always shown on test records (CLAUDE.md §33). Uses only
          existing design tokens. */}
      {prep.isTest && (
        <span style={{
          display: 'inline-block',
          marginBottom: '8px',
          marginRight: '6px',
          padding: '1px 7px',
          borderRadius: '8px',
          background: 'var(--cp-surface-alt)',
          border: '1px dashed var(--cp-warning)',
          color: 'var(--cp-warning)',
          fontSize: '10px',
          fontWeight: 700,
          fontFamily: "'Kalam', cursive",
          letterSpacing: '0.04em',
        }}>TEST</span>
      )}
      {prep.priority === 'urgent' && <span style={styles.urgentPill}>URGENT</span>}
      {/* Appointment date intentionally omitted from the pipeline card face —
          it remains visible in the full opened sheet and the submission form.
          Card face shows client / due date / task count only.
          Test records have no deadline (CLAUDE.md §33) — the "Due:" line is
          skipped entirely rather than showing "Due: —". */}
      {!prep.isTest && (
        <div style={{ ...styles.deadline, color: dueColour, fontWeight: urgency === 'overdue' ? 800 : 700 }}>
          Due: {formatPrepDeadline(deadline)}
          {urgency === 'overdue' && <span style={styles.overdueTag}>OVERDUE</span>}
        </div>
      )}
      <span style={styles.taskCount}>{taskBadge}</span>
    </div>
  );
}

const styles = {
  card: {
    position: 'relative',
    // Same top clearance a FolderCard reserves for its folder tab, so a prep
    // sheet that is first in its column starts at the same y as the file card
    // that is first in the next one (imported, never re-typed — see
    // CARD_TOP_CLEARANCE in FolderCard.jsx).
    marginTop: CARD_TOP_CLEARANCE,
    background: PAPER,
    borderRadius: '2px',
    padding: '20px 16px 16px 36px',
    minHeight: '120px',
    cursor: 'pointer',
    // Same soft elevation as the file FolderCards so it sits naturally.
    boxShadow: '0 2px 6px rgba(0,0,0,0.08), 0 8px 18px rgba(0,0,0,0.06)',
    borderLeft: `1px solid ${PAPER_EDGE}`,
    // Layer 1: red margin rule at 24–25px. Layer 2: blue ruled lines @20px.
    backgroundImage: `linear-gradient(to right, transparent 24px, ${MARGIN} 24px, ${MARGIN} 25px, transparent 25px), repeating-linear-gradient(to bottom, transparent 0, transparent 19px, ${RULED} 19px, ${RULED} 20px)`,
    backgroundPosition: '0 0, 0 16px',
  },
  // Acknowledgement illumination (CLAUDE.md §31) — amber border via outline
  // (avoids layout shift against the card's existing borderLeft). The ring
  // itself is animated by .inbox-ack-lit-paper in globals.css.
  cardLit: {
    outline: '1.5px solid var(--cp-ack-amber)',
    outlineOffset: '0px',
  },
  notesBadge: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    fontFamily: "'Kalam', cursive",
    fontSize: '10px',
    fontWeight: 700,
    color: INK_LIGHT,
    background: 'rgba(255,255,255,0.75)',
    border: `1px solid ${INK_LIGHT}`,
    borderRadius: '999px',
    padding: '1px 7px',
    lineHeight: 1.4,
  },
  pingWrap: {
    position: 'absolute',
    top: '-3px',
    right: '-3px',
    width: '8px',
    height: '8px',
  },
  pingRing: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    background: 'var(--cp-ack-amber)',
  },
  pingDot: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    background: 'var(--cp-ack-amber)',
    boxShadow: '0 0 0 1.5px #fff',
  },
  hole: {
    position: 'absolute',
    left: '11px',
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    background: '#F0F4F8',
    boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.3)',
  },
  client: {
    fontFamily: "'Caveat', cursive",
    fontSize: '22px',
    fontWeight: 700,
    color: INK,
    lineHeight: 1,
    marginBottom: '10px',
  },
  urgentPill: {
    display: 'inline-block',
    marginBottom: '8px',
    padding: '1px 7px',
    borderRadius: '8px',
    background: 'rgba(192, 57, 43, 0.1)',
    color: '#C0392B',
    fontSize: '10px',
    fontWeight: 700,
    fontFamily: "'Kalam', cursive",
    letterSpacing: '0.04em',
  },
  deadline: {
    fontFamily: "'Kalam', cursive",
    fontSize: '13px',
    lineHeight: '20px',
    marginTop: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  overdueTag: {
    fontFamily: "'Kalam', cursive",
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.06em',
    color: '#FFFFFF',
    background: OVERDUE_RED,
    borderRadius: '3px',
    padding: '1px 6px',
  },
  taskCount: {
    display: 'inline-block',
    marginTop: '12px',
    fontFamily: "'Kalam', cursive",
    fontSize: '12px',
    fontWeight: 700,
    color: INK_LIGHT,
    padding: '2px 9px',
    background: 'rgba(255,255,255,0.55)',
    border: `1px dashed ${INK_LIGHT}`,
    borderRadius: '3px',
  },
};
